/**
 * Concept-matched hero visuals for the welcome step. Each ads-v2 concept shows a visual
 * that sells ITS angle (jump, cost, voice, loop) instead of the generic board, so the
 * first-screen picture matches the ad the visitor just clicked. All reuse the `.agent-board`
 * frosted-panel shell for a consistent, on-brand look. Decorative (aria-hidden inside).
 */
import { AgentBoard } from "./AgentBoard";
import { BrandLogo } from "./icons";

function Titlebar({ summary, summaryClass }: { summary: string; summaryClass?: string }) {
  return (
    <div className="agent-board__titlebar" aria-hidden="true">
      <span className="agent-board__traffic" />
      <span className="agent-board__title">agentmax</span>
      <span className={summaryClass ? `agent-board__summary ${summaryClass}` : "agent-board__summary"}>{summary}</span>
    </div>
  );
}

/** Reach the one waiting: the board with the amber row lifted and a one-click "jump" affordance. */
function ReachWaitingVisual() {
  return (
    <div className="agent-board agent-board--jump" role="img" aria-label="AgentMax: jump to the agent that needs you">
      <Titlebar summary="1 needs you" />
      <div className="agent-board__rows" aria-hidden="true">
        <div className="agent-board__row is-ok">
          <span className="agent-board__pixel is-ok" />
          <span className="agent-board__name">claude code</span>
          <span className="agent-board__activity">refactoring checkout flow</span>
          <span className="agent-board__num">24m10s</span>
        </div>
        <div className="agent-board__row is-wait hero-jump__target">
          <span className="agent-board__pixel is-wait" />
          <span className="agent-board__name">codex</span>
          <span className="agent-board__activity">waiting for your approval</span>
          <span className="hero-jump__cta">jump to it <em>&#8594;</em></span>
        </div>
        <div className="agent-board__row is-idle">
          <span className="agent-board__pixel is-idle" />
          <span className="agent-board__name">cursor</span>
          <span className="agent-board__activity">done, 3 files changed</span>
          <span className="agent-board__num">12m40s</span>
        </div>
      </div>
      <div className="agent-board__foot" aria-hidden="true">
        <span>click the row, land in its terminal</span>
      </div>
    </div>
  );
}

/** Know the cost: per-agent costs summing into one big total. */
function CostVisual() {
  const chips = [
    { brand: "claude", cost: "$1.84" },
    { brand: "openai", cost: "$1.21" },
    { brand: "cursor", cost: "$0.74" },
  ] as const;
  return (
    <div className="agent-board agent-board--cost" role="img" aria-label="AgentMax: all your AI spend in one number">
      <Titlebar summary="today" />
      <div className="hero-cost__chips" aria-hidden="true">
        {chips.map((chip, index) => (
          <span className="hero-cost__group" key={chip.brand}>
            <span className="hero-cost__chip">
              <span className="hero-cost__logo">
                <BrandLogo name={chip.brand} />
              </span>
              <span className="hero-cost__chip-cost">{chip.cost}</span>
            </span>
            {index < chips.length - 1 ? <em className="hero-cost__plus">+</em> : null}
          </span>
        ))}
      </div>
      <div className="hero-cost__total" aria-hidden="true">
        <span className="hero-cost__label">total today, every agent</span>
        <strong className="hero-cost__value">$3.86</strong>
      </div>
      <div className="agent-board__foot" aria-hidden="true">
        <span>
          plan limit <span className="agent-board__hl">~6:00pm</span>
        </span>
      </div>
    </div>
  );
}

function MicGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6 11a6 6 0 0 0 12 0" />
      <path d="M12 17v3" />
    </svg>
  );
}

/** Voice for agents: mic + waveform + a spoken command. */
function VoiceVisual() {
  return (
    <div className="agent-board agent-board--voice" role="img" aria-label="AgentMax: talk to your agents, hands-free">
      <Titlebar summary="listening" summaryClass="hero-voice__badge" />
      <div className="hero-voice__body" aria-hidden="true">
        <span className="hero-voice__mic">
          <MicGlyph />
        </span>
        <span className="hero-voice__wave">
          {Array.from({ length: 9 }).map((_, index) => (
            <i key={index} />
          ))}
        </span>
      </div>
      <div className="hero-voice__quote" aria-hidden="true">&ldquo;approve the one that&rsquo;s waiting&rdquo;</div>
      <div className="agent-board__foot" aria-hidden="true">
        <span>on-device voice. nothing leaves your Mac.</span>
      </div>
    </div>
  );
}

/** Run it all day: an overnight cloud loop working a goal to completion. */
function LoopVisual() {
  const rows = [
    { status: "ok", label: "PR opened", state: "done" },
    { status: "ok", label: "tests passing", state: "done" },
    { status: "wait", label: "1 thing needs you", state: "review" },
  ] as const;
  return (
    <div className="agent-board agent-board--loop" role="img" aria-label="AgentMax: write it tonight, done by morning">
      <Titlebar summary="loop 214 · in the cloud" summaryClass="hero-loop__badge" />
      <div className="hero-loop__goal" aria-hidden="true">goal: ship the React 19 upgrade</div>
      <div className="agent-board__rows" aria-hidden="true">
        {rows.map((row) => (
          <div className={`agent-board__row is-${row.status} hero-loop__row`} key={row.label}>
            <span className={`agent-board__pixel is-${row.status}`} />
            <span className="agent-board__name">{row.label}</span>
            <span className="agent-board__num hero-loop__state">{row.state}</span>
          </div>
        ))}
      </div>
      <div className="agent-board__foot" aria-hidden="true">
        <span>running while you sleep</span>
      </div>
    </div>
  );
}

/**
 * Recorded product demo shown on the result + checkout steps (both the one-time and
 * free-trial offers). Encoded from agentmax-demo.gif: WebM ~0.6MB / MP4 ~1.1MB versus
 * 13MB for the GIF, which matters on the two highest-intent screens. The welcome hero
 * keeps the live CSS board so it still renders instantly above the fold.
 */
export type FunnelDemoImage = { src: string; alt: string; width: number; height: number };

export function FunnelDemo({ image }: { image?: FunnelDemoImage } = {}) {
  // A concept can swap the recording for a still that sells its own angle.
  if (image) {
    return (
      <img
        className="funnel-demo funnel-demo--image"
        src={image.src}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <video
      className="funnel-demo"
      poster="/assets/product/agentmax-demo-poster.jpg"
      width={1280}
      height={836}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      aria-label="AgentMax in action: every agent on one notch with live status, run time, and cost."
    >
      <source src="/assets/product/agentmax-demo.webm" type="video/webm" />
      <source src="/assets/product/agentmax-demo.mp4" type="video/mp4" />
    </video>
  );
}

export type HeroVisualVariant = "board" | "jump" | "cost" | "voice" | "loop";

/** Dispatches to the concept's hero visual; falls back to the live board (old concepts + "see every agent"). */
export function HeroVisual({ variant, agentsUsed }: { variant?: HeroVisualVariant; agentsUsed?: string }) {
  switch (variant) {
    case "jump":
      return <ReachWaitingVisual />;
    case "cost":
      return <CostVisual />;
    case "voice":
      return <VoiceVisual />;
    case "loop":
      return <LoopVisual />;
    default:
      return <AgentBoard agentsUsed={agentsUsed} />;
  }
}
